import React, { useEffect, useRef, useState } from 'react';
import { Post as PostType } from '../../../types';
import ImgProfilePost from '../ImgProfilePost';
import UserName from '../UserName';
import "./style.css";
import { censorText, clearText, extractFirstUrl, extractYouTubeId } from '../../../global-components/src/utils/text';
import OptionsPost from '../OptionsPost';
import ImgPost from '../ImgPost/ImgPost';
import Repost from '../Repost';
import { ContainerButtonAds } from '../../container-button-ads/container-button-ads';
import DropdownPost from '../dropdown-post';
import hljs from 'highlight.js';
import 'highlight.js/styles/github-dark.css';
import { Frame } from '../ImgProfilePost/frame';
import { useAuth, useTheme } from '../../../global-components/src';
import { YoutubePreview } from './youtube-preview';
import { LinkPreview } from './link-preview';
import { BUCKET_PACOCA_URL } from '../../../global-components/src/utils/vars';
import { useNavigate } from 'react-router-dom';
import PostPoll from './poll';
import { detectLanguage, isUserLang, translate } from '../../../global-components/src/utils/language';
import { t } from 'i18next';
import { PostLinkPreview } from './post-link-preview';
import { Volume2 } from 'lucide-react';
import { hasPosition } from '../../../global-components/src/utils/auth';
import { renderTextWithPhoneMessageLinks } from '../../../utils/phone-message-links';

interface PostProps {
    post: PostType;
    showFollowUser?: boolean;
    onRepost: () => void;
    setPosts?: React.Dispatch<React.SetStateAction<PostType[]>>;
    onEdit: (post: PostType) => void;
    onDelete: (post: PostType) => void;
    onRestore: (post: PostType) => void;
    onOpenLikes?: () => void;
    onOpenComments?: () => void;
    onCloseModals?: () => void;
    onReport: (removeReport: boolean, id_user: number, id_post: number) => void;
    onFollow?: (isFollow: boolean, idUser: number) => void;
}
 
const Post: React.FC<PostProps> = ({ post, showFollowUser, setPosts, onRepost, onEdit, onDelete, onReport, onRestore, onOpenLikes, onOpenComments, onCloseModals, onFollow }) => {
    const { isPacocagram, hideCensorship, isNewPacoca, } = useTheme();
    const renderPostText = (text?: string) => {
        if (!text) return "";
        return renderTextWithPhoneMessageLinks(
            !hideCensorship ? censorText(text) : text,
            clearText,
        );
    };
    const [processedText, setProcessedText] = useState<string>(renderPostText(post?.text));
    const [oldProcessedText, setOldProcessedText] = useState<string>(renderPostText(post?.text));
    const [translatedText, setTranslatedText] = useState<string>("");
    const [isTranslate, setIsTranslate] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [canTranslate, setCanTranslate] = useState<boolean>(false);
    const [canExpand, setCanExpand] = useState<boolean>(false);
    const textRef = useRef<HTMLParagraphElement>(null);
    const elementRef = useRef<HTMLDivElement>(null);
    const [openSettings, setOpenSettings] = useState(false);
    const youtubeId = extractYouTubeId(post?.text ?? "");
    const hasLink = extractFirstUrl(post?.text ?? "");
    const hasYoutube = Boolean(youtubeId);
    const hasImages = post?.images?.length > 0; 
    const navigate = useNavigate();
    const language = detectLanguage(post?.text);
    const isTextUserLang = isUserLang(language);
    const soundPlugin = !!sessionStorage.getItem("soundPlugin");
    const [orientation, setOrientation] = useState<"square" | "horizontal">("square");
    const { user } = useAuth();

    useEffect(() => {
        if(!isTextUserLang && !post.gif_url) translateText();
    }, [post?.text]);

    useEffect(() => {
        setProcessedText(renderPostText(post?.text));
        setOldProcessedText(renderPostText(post?.text));
    }, [post?.text]);

    useEffect(() => {
        if (textRef.current) {
            const realHeight = textRef.current.scrollHeight;
            const maxHeight = 100; // Mesmo valor de maxHeight do CSS
            // Verifica se o resultado final é apenas um emoji (img tag)
            const onlyEmoji = processedText.trim().match(/^<img[^>]*class=['"]emoji['"][^>]*>$/);
            setCanExpand(realHeight > (maxHeight + 100) && !onlyEmoji);
        }
    }, [post?.text]);

//    useEffect(() => {
//         hljs.configure({
//             ignoreUnescapedHTML: true
//         });

//         document.querySelectorAll('pre code').forEach((el: any) => {
//             delete el.dataset.highlighted;
//         });
//         const timeout = setTimeout(() => {
//             const blocks = document.querySelectorAll('pre code:not([data-highlighted])');
//             blocks.forEach((block: any) => {
//                 hljs.highlightElement(block);
//             });
//         }, 20);
//         return () => clearTimeout(timeout);
//     }, [processedText]);

    useEffect(() => {
        hljs.configure({ ignoreUnescapedHTML: true });

        // ✅ Usar elementRef.current para não varrer a página inteira
        const container = elementRef.current;
        if (!container) return;

        const timeout = setTimeout(() => {
            container.querySelectorAll('pre code').forEach((el: any) => {
                delete el.dataset.highlighted;
                hljs.highlightElement(el);
            });
        }, 20);

        return () => clearTimeout(timeout);
    }, [processedText]);

    const translateText = async(updateText = false) => {
        if(translatedText.length && updateText){
            setProcessedText(translatedText);
            setIsTranslate(true);
            return;
        }
        
        
        try {
            let translated = await translate(post.text);
            if(!translated || post?.text?.trim() == translated.trim()) return;
            translated = renderPostText(translated)
            if(!translated) return;
            setTranslatedText(translated)

            if(updateText){
                setProcessedText(translated);
                setIsTranslate(true);
            }
            setCanTranslate(true);
        } catch (err: any) {
            console.error('Translation error:', err?.response?.data || err);
            return null;
        }
    }

    const showOriginal = async() => {
        setProcessedText(oldProcessedText);
        setIsTranslate(false)
    }


    useEffect(() => {
        const newText = renderPostText(post?.text);
        setProcessedText(newText);
        setIsExpanded(false);
    }, [post.text]);


    useEffect(() => {
        if(processedText.includes('language')){
             const interval = setInterval(() => {
                document.querySelectorAll('pre code').forEach((el: any) => {
                    delete el.dataset.highlighted;
                });

                const blocks = document.querySelectorAll('pre code:not([data-highlighted])');
                blocks.forEach((block: any) => {
                    hljs.highlightElement(block);
                });
    
                if (textRef.current) {
                    const realHeight = textRef.current.scrollHeight;
                    const maxHeight = 200;
                    const onlyEmoji = processedText.trim().match(/^<img[^>]*class=['"]emoji['"][^>]*>$/);
                    setCanExpand(realHeight > maxHeight + 20 && !onlyEmoji);
                }
            }, 20); // roda a cada 20ms
    
            return () => clearInterval(interval); // limpa ao desmontar
        }
        
    }, [processedText, isExpanded]);

    useEffect(() => {
        if(!processedText.includes('language') && textRef.current){
            const realHeight = textRef.current.scrollHeight;
            const maxHeight = 200;
            const onlyEmoji = processedText.trim().match(/^<img[^>]*class=['"]emoji['"][^>]*>$/);
            setCanExpand(realHeight > maxHeight + 20 && !onlyEmoji);
        }
        
    }, [processedText, isExpanded]);

    return (
        <div key={`post-${post?.id}`} style={{ opacity: post.is_hidden && hasPosition(user) ? .7 : 1 }}>
            <div className="card card-post">
                <div className="row-post mb-2">
                    {post?.ads && (
                        <>
                            {post?.ads.status === "validando_conteudo" && `🔎 ${t("ads.landingPage.waitingApproval")}`}
                            {post?.ads.status.includes("Reprovado") && `❌ ${t("post.actions.reproveAds")}`}
                        </>
                    ) }
                </div>
                
                {/* HEADER */}
                <div className={`row row-post ${post.poem_id && post.poem_id > 0 ? "post-width-poem" : ""} ${post.user?.active_frame?.image_path && !post.group_id ? "row-post-with-frame" : ""}`} style={{ flexWrap: "nowrap" }}>
                    {/* overflow: "hidden" */}
                    <div className="row row-inside-post">
                        <ImgProfilePost post={post} />
                        {post.user?.active_frame?.image_path && !post.group_id &&
                            <Frame path={`${BUCKET_PACOCA_URL}/${post.user.active_frame?.image_path}`} className="cursor-pointer post"/>
                        }
                        <UserName showContentPost={isNewPacoca} onFollow={onFollow} showFollow={showFollowUser} post={post}>
                            {/* TEXTO */}
                            {post?.text && !isPacocagram && post.gif_url != post.text ? (
                                <div ref={elementRef}
                                    className={`row row-text-post ${canExpand ? "cursor-pointer" : ""}`}
                                    onClick={() => canExpand && setIsExpanded(prev => !prev)}
                                >
                                    {post.poem_title?.trim().length ? (
                                        <h6 className='title-poem'>{post.poem_title}</h6>
                                    ): null}
                                    <p
                                        ref={textRef}
                                        className="text-post text-post-no-pacocagram  post text-expand"
                                        style={{
                                            maxHeight: !isExpanded ? 200 : "none",
                                            overflow: !isExpanded ? "hidden" : "visible",
                                            transition: "max-height 0.25s ease"
                                        }}
                                        dangerouslySetInnerHTML={{ __html: processedText }}
                                    />
                                    
                                    {!!post.is_hidden && hasPosition(user) && (
                                        <>
                                            <p className="text-post text-post-no-pacocagram  post text-expand" style={{ fontWeight: "bold" }}>{t("post.hiddenByAiReason", { reason: post.hidden_reason })}</p>
                                        </>
                                    )}
                                    {!!post.deleted_at && !!post.deleted_reason && hasPosition(user) && (
                                        <>
                                            <p className="text-post text-post-no-pacocagram  post text-expand" style={{ fontWeight: "bold" }}>{t("post.deletedByReason", { reason: post.deleted_reason })}</p>
                                        </>
                                    )}

                                    {!isExpanded && canExpand ? (
                                        <span className="user-select-none point-post">
                                            <span>{t("post.actions.showMore")}</span>
                                        </span>
                                    ) : (
                                        <>
                                            {!isTextUserLang && canTranslate && !isTranslate && (
                                                <span className='user-select-none text-translate-post'>
                                                    <span onClick={() => translateText(true)}>{t("post.actions.translate")}</span>
                                                </span>
                                            )}
                                            {!isTextUserLang && canTranslate && isTranslate && (
                                                <span className='user-select-none text-translate-post'>
                                                    <span onClick={showOriginal}>{t("post.actions.untranslate")}</span>
                                                </span>
                                            )}
                                        </>

                                    )}
                                           
                                </div>
                            ) : null}

                            <div className={`row user-select-none row-img-post`}>
                                {post.gif_url && (
                                    <div className="col col-img-post">
                                        <img
                                            style={{ marginLeft: 0, width: "100%", height: "100%",}}
                                            src={post.gif_url}
                                            className="img-post cursor-pointer"
                                            alt="GIF"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* YOUTUBE TEM PRIORIDADE */}
                            {hasYoutube && !hasImages && !post.gif_url && (
                                <YoutubePreview text={post.text} />
                            )}

                            {/* LINK PREVIEW SÓ SE NÃO FOR YOUTUBE */}
                            {!hasYoutube && !hasImages && !post.gif_url && hasLink && (
                                <PostLinkPreview text={post.text} preview={post.link_preview} postId={post.id} />
                            )}

                            {post?.poll && (
                                <PostPoll
                                    poll={post.poll}
                                    postId={post.id}
                                />
                            )}

                            {/* IMAGEM */}
                            <ImgPost img_account={post?.user?.img_account} images={post?.images} post={post} onComment={onOpenComments} onLike={onOpenLikes} onShare={onRepost} setPosts={setPosts} />

                            {/* TEXTO */}
                            {post?.text && isPacocagram && post.gif_url != post.text ? ( 
                                <div ref={elementRef}
                                    className={`row row-text-post ${canExpand ? "cursor-pointer" : ""}`}
                                    onClick={() => canExpand && setIsExpanded(prev => !prev)}
                                >
                                    <p
                                        ref={textRef}
                                        className="text-post text-post-pacocagram  post text-expand"
                                        style={{
                                            maxHeight: !isExpanded ? 200 : "none",
                                            overflow: !isExpanded ? "hidden" : "visible",
                                            transition: "max-height 0.25s ease"
                                        }}
                                        dangerouslySetInnerHTML={{ __html: processedText }}
                                    />

                                    {!isExpanded && canExpand && (
                                        <span className="point-post">
                                            <span>{t("post.actions.showMore")}</span>
                                        </span>
                                    )}
                                </div>
                            ) : null}

                            {/* REPOST */}
                            {post?.original_post?.id_user && (
                                <Repost
                                    onOpenComments={onOpenComments}
                                    setPosts={setPosts}
                                    post={post.original_post}
                                    onReport={(r) => onReport(r, post.user?.id, post.id)}
                                    onEdit={() => onEdit(post.original_post)}
                                    onDelete={() => onDelete(post.original_post)}
                                />
                            )}

                            
                            {/* BOTÕES */}
                            <ContainerButtonAds post={post} />
                            <OptionsPost
                                setPosts={setPosts}
                                onCloseModals={onCloseModals}
                                onOpenComments={onOpenComments}
                                onOpenLikes={onOpenLikes}
                                post={post}
                                onRepost={onRepost}
                            />
                        </UserName>

                        
                    </div>
                    <DropdownPost
                        onDelete={onDelete}
                        onEdit={onEdit}
                        onReport={onReport}
                        post={post}
                        onRestore={onRestore}
                    />
                </div>

                {!isNewPacoca ? (
                    <div className="is-not-new-pacoca">
                        {post.poem_title?.trim().length ? (
                            <h6 className='title-poem'>{post.poem_title}</h6>
                        ): null}

                        {/* TEXTO */}
                        {post?.text && !isPacocagram && !post.gif_url ? (
                            <div ref={elementRef}
                                className={`row row-text-post ${canExpand ? "cursor-pointer" : ""}`}
                                onClick={() => canExpand && setIsExpanded(prev => !prev)}
                            >
                                <p
                                    ref={textRef}
                                    className="text-post text-post-no-pacocagram  post text-expand"
                                    style={{
                                        maxHeight: !isExpanded ? 200 : "none",
                                        overflow: !isExpanded ? "hidden" : "visible",
                                        transition: "max-height 0.25s ease"
                                    }}
                                    dangerouslySetInnerHTML={{ __html: processedText }}
                                />
                                {!!post.is_hidden && hasPosition(user) && (
                                    <>
                                        <p className="text-post text-post-no-pacocagram  post text-expand" style={{ fontWeight: "bold" }}>{t("post.hiddenByAiReason", { reason: post.hidden_reason })}</p>
                                    </>
                                )}
                                {!!post.deleted_at && !!post.deleted_reason && hasPosition(user) && (
                                    <>
                                        <p className="text-post text-post-no-pacocagram  post text-expand" style={{ fontWeight: "bold" }}>{t("post.deletedByReason", { reason: post.deleted_reason })}</p>
                                    </>
                                )}

                                {!isExpanded && canExpand ? (
                                    <span className="user-select-none point-post">
                                        <span>{t("post.actions.showMore")}</span>
                                    </span>
                                ) : (
                                    <>
                                        {!isTextUserLang && canTranslate && !isTranslate && (
                                            <span className='user-select-none text-translate-post'>
                                                <span onClick={() => translateText(true)}>{t("post.actions.translate")}</span>
                                            </span>
                                        )}
                                        {!isTextUserLang && canTranslate && isTranslate && (
                                            <span className='user-select-none text-translate-post'>
                                                <span onClick={showOriginal}>{t("post.actions.untranslate")}</span>
                                            </span>
                                        )}
                                    </>

                                )}
                            </div>
                        ) : (
                            (post?.images?.length > 0 || post?.original_post?.id_user) && (
                                <div style={{ height: 5 }}></div>
                            )
                        )}

                        {post.gif_url && (
                            <img
                                src={post.gif_url}
                                className={`post-gif ${orientation}`}
                                alt="GIF"
                                loading="lazy"
                                onLoad={(e) => {
                                    const img = e.currentTarget;
                                    const diff = img.naturalWidth - img.naturalHeight;

                                    if (Math.abs(diff) > 50 && img.naturalWidth > img.naturalHeight) {
                                        setOrientation("horizontal");
                                    }
                                }}
                            />
                        )}

                        {/* YOUTUBE TEM PRIORIDADE */}
                        {hasYoutube && !hasImages && !post.gif_url && (
                            <YoutubePreview text={post.text} />
                        )}

                        {/* LINK PREVIEW SÓ SE NÃO FOR YOUTUBE */}
                        {!hasYoutube && !hasImages && !post.gif_url && hasLink && (
                            <PostLinkPreview text={post.text} preview={post.link_preview} postId={post.id} />
                        )}

                        {post?.poll && (
                            <PostPoll
                                poll={post.poll}
                                postId={post.id}
                            />
                        )}

                        {/* IMAGEM */}
                        <ImgPost img_account={post?.user?.img_account} images={post?.images} post={post} onComment={onOpenComments} onLike={onOpenLikes} onShare={onRepost} setPosts={setPosts} />

                        {/* TEXTO */}
                        {post?.text && isPacocagram && !post.gif_url ? ( 
                            <div ref={elementRef}
                                className={`row row-text-post ${canExpand ? "cursor-pointer" : ""}`}
                                onClick={() => canExpand && setIsExpanded(prev => !prev)}
                            >   
                                {post.poem_title?.trim().length ? (
                                    <h6 className='title-poem'>{post.poem_title}</h6>
                                ): null}
                                <p
                                    ref={textRef}
                                    className="text-post text-post-pacocagram  post text-expand"
                                    style={{
                                        maxHeight: !isExpanded ? 200 : "none",
                                        overflow: !isExpanded ? "hidden" : "visible",
                                        transition: "max-height 0.25s ease"
                                    }}
                                    dangerouslySetInnerHTML={{ __html: "processedText" }}
                                />
                                {!!post.is_hidden && hasPosition(user) && (
                                    <>
                                        <p className="text-post text-post-no-pacocagram  post text-expand" style={{ fontWeight: "bold" }}>{t("post.hiddenByAiReason", { reason: post.hidden_reason })}</p>
                                    </>
                                )}
                                {!!post.deleted_at && !!post.deleted_reason && hasPosition(user) && (
                                    <>
                                        <p className="text-post text-post-no-pacocagram  post text-expand" style={{ fontWeight: "bold" }}>{t("post.deletedByReason", { reason: post.deleted_reason })}</p>
                                    </>
                                )}

                                {!isExpanded && canExpand && (
                                    <span className="point-post">
                                        <span>{t("post.actions.showMore")}</span>
                                    </span>
                                )}
                            </div>
                        ) : (
                            (post?.images?.length > 0 || post?.original_post?.id_user) && (
                                <div style={{ height: 5 }}></div>
                            )
                        )}

                        {/* REPOST */}
                        {post?.original_post?.id_user && (
                            <Repost
                                onOpenComments={onOpenComments}
                                setPosts={setPosts}
                                post={post.original_post}
                                onReport={(r) => onReport(r, post.user?.id, post.id)}
                                onEdit={() => onEdit(post.original_post)}
                                onDelete={() => onDelete(post.original_post)}
                            />
                        )}

                        {/* BOTÕES */}
                        <ContainerButtonAds post={post} />
                        <OptionsPost
                            setPosts={setPosts}
                            onCloseModals={onCloseModals}
                            onOpenComments={onOpenComments}
                            onOpenLikes={onOpenLikes}
                            post={post}
                            onRepost={onRepost}
                        />
                    </div>
                ) : null}
            </div> 
        </div>
    );
};


export default React.memo(Post);